<script context="module">
  const revealedTokens = new Set();
  const refreshedTokens = new Set();
</script>

<script>
  import {
    createRandomCleanEncoding,
    encodingToHex,
    hexToEncoding,
  } from "bitframes";
  import { currentVideo } from "./components/VideoEmbed.svelte";
  import LeadersPage from "./components/LeadersPage.svelte";
  import Modal from "./components/Modal.svelte";
  import HelpContainer from "./components/HelpContainer.svelte";
  import Toast from "./components/Toast.svelte";
  import { onDestroy, onMount, setContext } from "svelte";
  import { applyRandomEdit, randomWithLocks } from "./util/randomizer.js";
  import * as History from "./util/history.js";
  import PrintModal from "./components/PrintModal.svelte";

  import { Route, Router, navigate } from "svelte-routing";
  import Nav from "./components/Nav.svelte";
  import Home from "./components/Home.svelte";
  import RouterContext from "./RouterContext.svelte";
  import * as local from "./util/local.js";
  import { get, writable } from "svelte/store";
  import SimpleGallery from "./components/SimpleGallery.svelte";
  import GalleryView from "./components/GalleryView.svelte";
  import About from "./components/About.svelte";
  import EpisodePage from "./components/EpisodePage.svelte";

  import {
    IS_PRE_LAUNCH,
    isBeforeLaunchStore,
    LINK_ABOUT,
    LINK_CREATE,
    LINK_CREATE_EDITION,
    LINK_DONATE,
    LINK_EDITIONS,
    LINK_EPISODES,
    LINK_GALLERY,
    LINK_HOME,
    LINK_TERMS,
    PATH_CREATE_EDITION,
  } from "./util/constants";
  import MintModal from "./components/MintModal.svelte";
  import * as hotkeys from "./util/hotkeys";
  import MintRevealModal from "./components/MintRevealModal.svelte";
  import EditPage from "./components/EditPage.svelte";
  import TermsAndConditions from "./components/TermsAndConditions.svelte";
  import ColorPickerPopup from "./components/ColorPickerPopup.svelte";
  import DonatePage from "./components/DonatePage.svelte";
  import { scrollPositions } from "./util/anchor-link";
  import PreLaunchGallery from "./components/PreLaunchGallery.svelte";
  import { ChunkType, decode_iTXt, readChunks } from "png-tools";
  import PagedGallery from "./components/PagedGallery.svelte";
  import RefreshTokenModal from "./components/RefreshTokenModal.svelte";
  import RefreshTokenRevealModal from "./components/RefreshTokenRevealModal.svelte";

  let system = 0x00;
  if (new URLSearchParams(window.location.search).get("system") == "1") {
    system = 0x01;
  }

  // TODO: remove on other pages
  // document.body.classList.add("no-scroll");
  window.history.scrollRestoration = "manual";

  function getDefaultEncoding() {
    // 1. Try to extract from URL
    let encBytes;

    const params = new URLSearchParams(window.location.search);
    if (params.get("encoding")) {
      try {
        encBytes = hexToEncoding(params.get("encoding"));
      } catch (err) {
        console.warn(`Error decoding URL parameter encoding:`, err.message);
      }
    }

    // 2. Try to extract local storage encoding
    // removed this to avoid likelihood of multiple same-seed mints
    // if (!encBytes) {
    //   const enc = local.get("encoding", null);
    //   if (enc) {
    //     try {
    //       encBytes = hexToEncoding(enc);
    //     } catch (err) {
    //       console.warn(`Error decoding local storage encoding:`, err.message);
    //     }
    //   }
    // }

    // 3. default
    if (!encBytes) {
      const curSystem =
        window.location.pathname === PATH_CREATE_EDITION ? 0x01 : system;

      encBytes = createRandomCleanEncoding(undefined, { system: curSystem });
    }

    return encBytes;
  }

  let encoding = getDefaultEncoding();
  const params = new URLSearchParams(window.location.search);
  const share = params.get("share");
  if (share === "" || share === "true") clearParams();

  let encodingStore = writable(encoding);
  console.log("Encoding:", encodingToHex(encoding));
  setContext("encoding", encodingStore);

  const encodingPreviewStore = writable(null);
  setContext("encoding-preview", encodingPreviewStore);

  const MAX_UNDO_REDO = 1000;
  let allowUndoRedo = false;
  const undoRedo = {
    current: null,
    undo: [],
    redo: [],
  };

  const undoRedoContext = {
    undo,
    redo,
    state: writable({
      canUndo: false,
      canRedo: false,
    }),
  };
  setContext("undo-redo", undoRedoContext);

  let fitHeight = false;
  let showGalleryToolbar = false;
  let stickyNav = true;

  const encodingsDiffer = (a, b) => {
    if (a == null || b == null) return a != b;

    const h0 = typeof a === "string" ? a : encodingToHex(a);
    const h1 = typeof b === "string" ? b : encodingToHex(b);
    return h0 !== h1;
  };

  let _lastEncoding = null;
  $: if (encoding && encodingsDiffer(_lastEncoding, encoding)) {
    if (_lastEncoding) {
      // console.log("Encoding Change:", encodingToHex(encoding));
      encodingStore.update(() => encoding);
    }
    _lastEncoding = encoding;
  }

  function updateUndoRedoState() {
    undoRedoContext.state.update(() => {
      return {
        canUndo: undoRedo.undo.length > 0,
        canRedo: undoRedo.redo.length > 0,
      };
    });
  }

  function undo() {
    if (undoRedo.undo.length > 0) {
      if (undoRedo.current) undoRedo.redo.push(undoRedo.current);
      undoRedo.current = null; // mark current as empty so next state change will receive it
      undoRedo.modifying = true;
      encoding = hexToEncoding(undoRedo.undo.pop());
    }
  }

  function redo() {
    if (undoRedo.redo.length > 0) {
      const next = undoRedo.redo.pop();
      if (undoRedo.current) undoRedo.undo.push(undoRedo.current);
      undoRedo.current = null;
      undoRedo.modifying = true;
      encoding = hexToEncoding(next);
    }
  }

  function keydown(ev) {
    if (allowUndoRedo && hotkeys.isEnabled()) {
      if (ev.key == "z" && !ev.shiftKey && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        undo();
      } else if (ev.key == "z" && ev.shiftKey && (ev.metaKey || ev.ctrlKey)) {
        ev.preventDefault();
        redo();
      }
    }
  }
  onMount(() => {
    const detachDrop = attachEncodingDrop();
    window.addEventListener("highlight:minted", mintedToken, { passive: true });
    window.addEventListener("highlight:seed-refresh-finished", updatedToken, {
      passive: true,
    });
    window.addEventListener("keydown", keydown, { passive: false });

    const unsub = encodingStore.subscribe((enc) => {
      const hex = encodingToHex(enc);
      History.push(hex);

      if (undoRedo.current) {
        if (undoRedo.undo.length > MAX_UNDO_REDO) undoRedo.undo.shift();
        undoRedo.undo.push(undoRedo.current);
      }
      if (!undoRedo.modifying) undoRedo.redo.length = 0;
      undoRedo.current = hex;
      undoRedo.modifying = false;
      updateUndoRedoState();

      // each time the encoding is adjusted or changed, also reset preview
      encodingPreviewStore.update(() => null);

      // updateUndoRedo();
      // console.log(undoRedo);
    });
    return () => {
      detachDrop();
      unsub();
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("highlight:minted", mintedToken);
      window.removeEventListener(
        "highlight:seed-refresh-finished",
        updatedToken
      );
    };
  });
  // encodingStore.subscribe((e) => console.log("New encoding:", e));

  function clearParams() {
    const url = new URL(window.location.href);
    if (url.search) {
      url.search = "";
      history.replaceState(null, "", url);
    }
  }

  $: local.set("encoding", encodingToHex(encoding));

  const lockedLayers = writable(Array(5).fill(false));
  setContext("lockedLayers", lockedLayers);
  // onDestroy(
  //   lockedLayers.subscribe((locks) => local.set("locked-layers", locks))
  // );

  const gallery = {
    mode: writable("gallery"),
    compact: writable(local.get("gallery:compact", true)),
    order: writable(local.get("gallery:sort-order", "asc")),
    address: writable(null),
    token: writable(null),
    range: writable(null),
  };
  const galleryCompact = gallery.compact;
  const galleryOrder = gallery.order;
  setContext("gallery", gallery);
  $: {
    let isCompact = $galleryCompact;
    local.set("gallery:compact", isCompact);
  }
  $: {
    let gallerySort = $galleryOrder;
    local.set("gallery:sort-order", gallerySort === "desc" ? "desc" : "asc");
  }
  // window.toggleOrder = () => {
  //   gallery.order.update((d) => (d === "asc" ? "desc" : "asc"));
  // };

  setContext("location", {
    setLocation(loc, changed) {
      if (loc === `/${LINK_HOME}` && changed) {
        randomize();
      }
      showGalleryToolbar = loc.startsWith("/gallery");
      if (showGalleryToolbar) {
        const end = loc.replace("/gallery/", "");
        if (end.startsWith("token")) {
          let tokenId = parseInt(end.replace("token/", ""), 10);
          if (!isFinite(tokenId) || tokenId < 0) tokenId = null;
          gallery.mode.update(() => "token");
          gallery.token.update(() => tokenId);
          gallery.address.update(() => null);
        } else if (end.startsWith("address")) {
          const addr = end.replace("address/", "");
          gallery.mode.update(() => "address");
          gallery.token.update(() => null);
          gallery.address.update(() => addr);
        } else {
          gallery.mode.update(() => "gallery");
          gallery.token.update(() => null);
          gallery.address.update(() => null);
        }
      }

      fitHeight =
        loc.startsWith(`/${LINK_CREATE}`) ||
        loc.startsWith(`/${LINK_GALLERY}/token`) ||
        loc.startsWith(`/${LINK_EDITIONS}/token`);

      allowUndoRedo =
        loc.startsWith(`/${LINK_CREATE}`) || loc === `/${LINK_HOME}`;

      document.body.style.height = fitHeight ? "100%" : "";
      document.documentElement.style.height = fitHeight ? "100%" : "";

      if (loc.startsWith(`/${LINK_CREATE}`)) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }

      if (changed) {
        const pos = scrollPositions.get(window.location.pathname);
        // console.log("setting pos", document.documentElement.scrollTop, pos);
        requestAnimationFrame(() => (document.documentElement.scrollTop = pos));
        // console.log("pos set", document.documentElement.scrollTop);
      }

      // console.log("LOC", loc);
      // scrollPositions.set(loc)
    },
  });

  let toast = null;
  let toastOpen = false;
  let modal = null;
  let _modalOpts = {};
  let popup = null;
  let _popupOpts = {};
  let _curPopupCallback = null;

  $: {
    if (modal) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
  }

  setContext("modal", {
    show: showModal,
    close: closeModal,
  });

  setContext("popup", {
    isOpen() {
      return Boolean(popup);
    },
    getCurrentPopup() {
      return {
        id: popup,
        options: { ..._popupOpts },
      };
    },
    select(id, opts = {}) {
      popup = id;
      _popupOpts = opts;

      if (_curPopupCallback) {
        _curPopupCallback(null);
      }

      return new Promise((cb) => {
        _curPopupCallback = cb;
      });
    },
    cancel: cancelPopup,
  });

  function cancelPopup() {
    popup = null;
    _popupOpts = null;
    if (_curPopupCallback) _curPopupCallback(null);
    _curPopupCallback = null;
  }

  setContext("toast", {
    show(text) {
      toast = { value: text, error: false };
      toastOpen = true;
    },
    error(text) {
      toast = { value: text, error: true };
      toastOpen = true;
    },
  });

  function showModal(id, opts = {}) {
    // document.body.classList.add("modal-open");
    modal = id;
    _modalOpts = opts;
    hotkeys.setEnabled(false);
    const curVideo = get(currentVideo);
    if (curVideo && curVideo.player.elem) {
      curVideo.player.pause();
    }
  }

  function closeModal() {
    // document.body.classList.remove("modal-open");
    modal = null;
    _modalOpts = {};
    hotkeys.setEnabled(true);
  }

  function encodingChanged(ev) {
    // const oldEncoding = encoding;
    const newEnc = ev.detail;
    encoding = newEnc;
    // console.log("CHANGE", encoding);
    // applyRandomEdit(oldEncoding, encoding, get(lockedLayers));
  }

  function randomize(ev = {}) {
    const defaultSystem = system;
    const curSystem =
      ev && ev.detail && ev.detail.edition ? 0x01 : defaultSystem;
    if (ev && ev.detail && ev.detail.withLocks) {
      encoding = randomWithLocks(encoding, {
        system: curSystem,
        lockedLayers: get(lockedLayers),
      });
    } else {
      encoding = createRandomCleanEncoding(undefined, { system: curSystem });
    }
  }

  function updatedToken(event) {
    const { seed, tokenId, txHash } = event.detail;
    console.log("highlight:updated", event.detail);
    const id = /^0x/i.test(tokenId)
      ? parseInt(tokenId, 16)
      : parseInt(tokenId, 10);
    if (isFinite(id) && id >= 0) {
      if (refreshedTokens.has(txHash)) {
        console.log("Already received refresh:", event.detail);
      } else {
        refreshedTokens.add(txHash);
        closeModal();
        navigate(
          `/${LINK_EDITIONS}/token/${id}?reveal=${txHash}&refresh=${seed.replace(/^0x/i, "")}`
        );
        showModal("reveal-update", {
          title: "Token Updated",
          tokenId: id,
          txHash,
          seed,
        });
      }
    }
  }

  function mintedToken(event) {
    const {
      // encoding,
      // publisher,
      // chainId,
      // collectionId,
      // contractAddress,
      tokenIds,
      txHash,
    } = event.detail;
    if (tokenIds.length == 0) return;
    console.log("highlight:minted", event.detail);

    const id = parseInt(tokenIds[0], 10);
    if (isFinite(id) && id >= 0) {
      if (revealedTokens.has(id)) {
        console.log("Already revealed token:", id);
      } else {
        revealedTokens.add(id);
        closeModal();
        navigate(`/${LINK_GALLERY}/token/${id}?reveal=${txHash}`);
        showModal("reveal", { title: "Mint", tokenId: id, txHash });
      }
    }
  }

  function attachEncodingDrop({ container = window } = {}) {
    async function handlerFunction(ev) {
      ev.preventDefault();
      if (ev.type === "drop") {
        let dt = ev.dataTransfer;
        let files = dt.files;
        if (!files.length) return;
        const file = files[0];

        try {
          if (file.name.endsWith(".png")) {
            const fileEncoding = await parseEncodingHexFromFile(file);
            if (fileEncoding) {
              encoding = hexToEncoding(fileEncoding);
            } else {
              const match = /([0-9a-fA-F]{64})\.png$/.exec(file.name);
              if (match) {
                encoding = hexToEncoding(match[1]);
              }
            }
          }
        } catch (err) {
          console.warn(err);
        }
      }
    }

    container.addEventListener("dragenter", handlerFunction, false);
    container.addEventListener("dragleave", handlerFunction, false);
    container.addEventListener("dragover", handlerFunction, false);
    container.addEventListener("drop", handlerFunction, false);
    return () => {
      container.removeEventListener("dragenter", handlerFunction, false);
      container.removeEventListener("dragleave", handlerFunction, false);
      container.removeEventListener("dragover", handlerFunction, false);
      container.removeEventListener("drop", handlerFunction, false);
    };
  }

  async function parseEncodingHexFromFile(file) {
    return new Promise((resolve) => {
      let reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        try {
          const chunks = readChunks(new Uint8Array(reader.result), {
            copy: false,
          });

          let foundEncoding = null;
          for (let c of chunks) {
            if (c.type == ChunkType.iTXt) {
              const d = decode_iTXt(c.data);
              if (d.keyword === "encoding") {
                foundEncoding = d.text;
                break;
              }
            }
          }
          resolve(foundEncoding);
        } catch (err) {
          resolve(null);
        }
      };
      reader.onerror = () => {
        console.warn("could not parse");
        resolve(null);
      };
    });
  }
</script>

<Router viewtransition={null}>
  <RouterContext>
    <Nav sticky={stickyNav} toolbar={showGalleryToolbar} />
    <div class="route-container">
      <Route path={`/${LINK_HOME}`}>
        <Home on:randomize={randomize}></Home>
      </Route>
      <Route path={`/${LINK_ABOUT}`}>
        <About></About>
      </Route>
      <Route path={`/${LINK_TERMS}`}>
        <TermsAndConditions></TermsAndConditions>
      </Route>
      <Route path={`/${LINK_DONATE}`}>
        <DonatePage></DonatePage>
      </Route>
      <Route path={`/${LINK_EDITIONS}`}>
        <LeadersPage></LeadersPage>
      </Route>
      <Route path={`/${LINK_EDITIONS}/token/:index`} let:params>
        <GalleryView system={0x01} index={parseInt(params.index, 10)}
        ></GalleryView>
      </Route>
      <Route path={`/${LINK_GALLERY}/token/:index`} let:params>
        <GalleryView
          on:encodingChanged={encodingChanged}
          index={parseInt(params.index, 10)}
        ></GalleryView>
      </Route>
      <Route path={`/${LINK_GALLERY}/address/:address`} let:params>
        <SimpleGallery address={params.address}></SimpleGallery>
      </Route>
      <Route path={`/${LINK_GALLERY}`}>
        <SimpleGallery></SimpleGallery>
      </Route>
      <Route path={`/${LINK_EPISODES}`} let:params>
        <EpisodePage></EpisodePage>
      </Route>
      <Route path={`/${LINK_EPISODES}/:episode`} let:params>
        <EpisodePage></EpisodePage>
      </Route>
      <Route path={`/${LINK_CREATE}`}>
        <!-- <Create on:randomize={randomize} on:encodingChanged={encodingChanged}
        ></Create> -->
        <EditPage
          edition={false}
          on:undo={undo}
          on:redo={redo}
          on:randomize={randomize}
          on:encodingChanged={encodingChanged}
        />
      </Route>
      <Route path={`/${LINK_CREATE_EDITION}/:id`} let:params>
        <!-- <Create on:randomize={randomize} on:encodingChanged={encodingChanged}
        ></Create> -->
        <EditPage
          editionID={parseInt(params.id, 10)}
          edition={true}
          on:undo={undo}
          on:redo={redo}
          on:randomize={randomize}
          on:encodingChanged={encodingChanged}
        />
      </Route>
    </div>
  </RouterContext>
</Router>

{#if popup == "color"}
  <ColorPickerPopup
    {..._popupOpts}
    on:cancel={cancelPopup}
    on:select={(ev) => {
      const result = ev.detail;
      if (_curPopupCallback) {
        _curPopupCallback(result);
        _curPopupCallback = null;
      }
      cancelPopup();
    }}
  />
{/if}

{#if modal}
  <Modal title={_modalOpts.title} on:close={closeModal}>
    {#if modal === "print"}
      <PrintModal />
    {:else if modal === "mint"}
      <MintModal />
    {:else if modal === "refresh"}
      <RefreshTokenModal editionID={_modalOpts.editionID} />
    {:else if modal === "reveal-update"}
      <RefreshTokenRevealModal
        on:close={closeModal}
        tokenId={_modalOpts.tokenId}
        txHash={_modalOpts.txHash}
        seed={_modalOpts.seed}
      />
    {:else if modal === "reveal"}
      {#key _modalOpts.txHash}
        <MintRevealModal
          on:close={closeModal}
          tokenId={_modalOpts.tokenId}
          txHash={_modalOpts.txHash}
        />
      {/key}
      <!-- <div>minted! #{_modalOpts.tokenId}</div> -->
    {:else if modal === "help"}
      <HelpContainer />
    {/if}
  </Modal>
{/if}

{#if toast && toastOpen}
  <Toast
    on:close={() => {
      toast = null;
      toastOpen = false;
    }}
    text={toast.value}
    error={toast.error}
  ></Toast>
{/if}

<style>
  .route-container {
    flex-grow: 1;
    /* align-items: center; */
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: auto;
    align-items: center;
    justify-content: stretch;
    width: 100%;
    /* max-width: var(--max-width); */
  }
  /* .fitHeight {
    height: 100vh;
  } */
</style>
